export type RolloutArm = 'stable' | 'canary';

export interface RolloutInfo {
  arm: RolloutArm;
  epoch: number;
}

export interface RolloutReaderOptions {
  getCookie?: () => string;
}

const COOKIE_NAME = 'voc_rollout';
const COOKIE_VALUE_PATTERN = /^(\d+)\.(stable|canary)$/;

/**
 * The FIRST read per page load — assigned or not — is frozen. A cookie appearing or
 * changing mid-session (QA override on a subresource, canary-failure flip) must never
 * retag a page that is already running the other arm's build, so no session ever
 * contributes events to both arms.
 */
let hasReadCookie = false;
let memoizedInfo: RolloutInfo | null = null;

/**
 * Parses the rollout assignment set by the rollout-router Worker out of a raw cookie
 * string (`voc_rollout={epoch}.{arm}`, e.g. `3.canary`). Returns null when the cookie
 * is absent or fails strict validation — silently, so environments the Worker never
 * touches (local dev, direct pages.dev visits) produce no console noise.
 */
export function parseRolloutCookie(cookieString: string): RolloutInfo | null {
  for (const pair of cookieString.split(';')) {
    const separator = pair.indexOf('=');
    if (separator === -1 || pair.slice(0, separator).trim() !== COOKIE_NAME) continue;

    let value = pair.slice(separator + 1).trim();
    try {
      value = decodeURIComponent(value);
    } catch {
      return null;
    }

    const match = COOKIE_VALUE_PATTERN.exec(value);
    return match ? { epoch: Number(match[1]), arm: match[2] as RolloutArm } : null;
  }
  return null;
}

export function getRolloutInfo(options: RolloutReaderOptions = {}): RolloutInfo | null {
  if (hasReadCookie) return memoizedInfo;

  // `?? ''` also guards partial DOM stubs (node tests) whose document has no cookie jar.
  const getCookie = options.getCookie ?? (() => (
    typeof document === 'undefined' ? '' : document.cookie ?? ''
  ));
  memoizedInfo = parseRolloutCookie(getCookie());
  hasReadCookie = true;
  return memoizedInfo;
}

/** Test-only: clears the page-lifetime memo so each test starts unread. */
export function __resetRolloutMemoForTests(): void {
  hasReadCookie = false;
  memoizedInfo = null;
}

export function getRolloutArm(options?: RolloutReaderOptions): RolloutArm | null {
  return getRolloutInfo(options)?.arm ?? null;
}

export function getRolloutEpoch(options?: RolloutReaderOptions): number | null {
  return getRolloutInfo(options)?.epoch ?? null;
}
